import React, { useRef, useState } from 'react';
import { API } from 'api';
import { DndContext, closestCenter } from '@dnd-kit/core';

import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove
} from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';
import {
  FileContainer,
  ProgressBarContainer,
  ProgressBarFill,
  ProgressBarItem,
  StyledButton,
  Title,
  TitleContainer,
  UploadContainer,
  UploadFileItem,
  UploadLabel,
  HelpText,
  WarningText,
  UploadInfo,
  ButtonContainer,
  StartButton,
  UploadFileItemInfo,
  UploadFiles,
  ProgressBarText,
  RemoveButton,
  StyledSelect
} from './FileUploader.styles';
import { Icon } from '../_shared/Icon';
import { events } from '../../_events';
import { useSelector } from 'react-redux';
import { selectGreenhouse } from '../../state';

export const FileUploader = ({
  customer,
  location,
  mission,
  plants,
  cropPipelines,
  devices = [],
  plant,
  cropPipeline,
  device,
  onUploadComplete,
  uploadedFiles
}) => {
  const uploadRefs = useRef({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [selectedPlant, setSelectedPlant] = useState(plant);
  const [selectedCropPipeline, setSelectedCropPipeline] = useState(cropPipeline);
  const [selectedDevice, setSelectedDevice] = useState(device);
  const selectedDeviceInfo = devices.find(item => item.name === selectedDevice);
  const fileInputRef = useRef();
  const allowedTypes = ['video/mp4', 'application/zip'];
  const RENAMED_FILE_REGEX = /^(.+?_)+(\d)_(.*)\.([^.]+)$/i;
  const { locations } = useSelector(selectGreenhouse);
  const getFileExtension = fileName => {
    const match = fileName.match(/\.([^.]+)$/);
    return match ? match[1].toLowerCase() : '';
  };
  function generateMissionId() {
    const length = 20;
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);

    for (let i = 0; i < length; i++) {
      result += charset[array[i] % charset.length];
    }

    return `grower_${result}`;
  }

  const checkTelemetry = async file => {
    const chunk = file.slice(0, 1024); // first 2MB
    const buffer = await chunk.arrayBuffer();
    const text = new TextDecoder().decode(buffer);
    const telemetryMarker = 'GPRO';

    return text.includes(telemetryMarker);
  };
  const reindexFiles = files => {
    return files.map((item, index) => {
      const addition = uploadedFiles ? uploadedFiles.length : 0;
      const extension = getFileExtension(item.file.name);
      const newName = buildFileName(location, addition + index + 1, extension);

      return {
        ...item,
        file: new File([item.file], newName, {
          type: item.file.type
        })
      };
    });
  };

  const SortableItem = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id
    });

    const style = {
      display: 'flex',
      transform: CSS.Transform.toString(transform),
      transition
    };

    return (
      <div ref={setNodeRef} style={style}>
        {children}
        <div
          {...attributes}
          {...listeners}
          style={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          ⠿
        </div>
      </div>
    );
  };

  const handleDragEnd = event => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setSelectedFiles(prev => {
      const oldIndex = prev.findIndex(f => f.file.name === active.id);
      const newIndex = prev.findIndex(f => f.file.name === over.id);

      const reordered = arrayMove(prev, oldIndex, newIndex);

      return reindexFiles(reordered); // 👈 important
    });
  };

  const uploadFile = (file, index, mission) => {
    updateUploadStatus(index, true);

    return API.upload
      .getUploadURL(customer, location, file.file.name, file.file.type, mission)
      .then(response => {
        const { upload_url, file_url } = response.data;
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', upload_url, true);
          uploadRefs.current[file.file.name] = xhr;
          xhr.setRequestHeader('Content-Type', file.file.type);
          xhr.withCredentials = true;

          xhr.upload.onprogress = event => {
            if (event.lengthComputable) {
              const percent = (event.loaded / event.total) * 100;
              updateUploadStatus(index, percent.toFixed(2), true);
            }
          };

          xhr.onload = () => {
            if (xhr.status === 200) {
              updateUploadStatus(index, 100, false);
              resolve(file_url);
            } else {
              updateUploadStatus(index, -1, false);
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          };

          xhr.onerror = () => {
            updateUploadStatus(index, -1, false);
            reject(new Error('Network error during upload'));
          };

          xhr.send(file.file);
        });
      });
  };

  const cancelUpload = () => {
    Object.values(uploadRefs.current).forEach(xhr => {
      if (xhr) xhr.abort();
    });
    setSelectedFiles([]);
    setUploading(false);
  };

  const updateUploadStatus = (index, percent, status) => {
    setSelectedFiles(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, progress: percent, uploading: status } : item
      )
    );
  };

  const handleFileChange = async e => {
    const newFiles = Array.from(e.target.files);
    const addition = uploadedFiles ? uploadedFiles.length : 0;
    setSuccess(false);
    if (!newFiles.length) return;

    const hasInvalidTypes = newFiles.some(file => !allowedTypes.includes(file.type));
    if (hasInvalidTypes) {
      events.toggleErrorModal(`Only ${allowedTypes} files are allowed.`);
      e.target.value = '';
      return;
    }
    if (selectedDeviceInfo) {
      const allFilesMatchDevice = newFiles.every(
        file => file.type === selectedDeviceInfo.accepted_type
      );
      if (!allFilesMatchDevice) {
        events.toggleErrorModal(
          `Selected files does not match to device accepted type: ${selectedDeviceInfo.accepted_type}`
        );
        e.target.value = '';
        return;
      }
    }
    const oldFilesNameExtension =
      uploadedFiles && uploadedFiles.length !== 0
        ? getFileExtension(uploadedFiles[0])
        : null;
    const newFilesNameExtension = getFileExtension(newFiles[0].name);
    if (oldFilesNameExtension && oldFilesNameExtension !== newFilesNameExtension) {
      events.toggleErrorModal(
        `Files type must match to types already existing in mission.`
      );
      e.target.value = '';
      return;
    }
    const newFileType = newFiles[0].type;
    const allFilesSameType = newFiles.every(file => file.type === newFileType);
    if (!allFilesSameType) {
      events.toggleErrorModal(`All files in one mission must be same type.`);
      e.target.value = '';
      return;
    }
    if (uploadedFiles) {
      const hasSameFile = newFiles.some(file =>
        uploadedFiles.includes(file.name.toLowerCase())
      );
      if (hasSameFile) {
        events.toggleErrorModal(
          'One of selected files was already uploaded, please select another one'
        );
        e.target.value = '';
        return;
      }
    }
    for (const file of newFiles) {
      if (file.type !== 'video/mp4') continue;
      const hasTelemetry = await checkTelemetry(file);
      if (!hasTelemetry) {
        events.toggleErrorModal(
          `Video "${file.name}" does not appear to contain telemetry data.`
        );
        return;
      }
    }
    const renamedFiles = newFiles.map((file, index) => {
      const expectedIndex = selectedFiles.length + index + 1 + addition;

      const match = file.name.match(RENAMED_FILE_REGEX);
      if (match) {
        const existingIndex = Number(match[2]);

        if (existingIndex !== expectedIndex) {
          events.toggleErrorModal(
            `File "${file.name}" looks like video #${existingIndex}, but you are uploading it as #${expectedIndex}.
  Please check the upload order.`
          );
        }
      }
      const extension = getFileExtension(file.name);
      const newName = buildFileName(location, expectedIndex, extension);

      return {
        file: new File([file], newName, { type: file.type }),
        progress: 0,
        uploading: false,
        originalName: file.name
      };
    });

    setSelectedFiles(prevFiles => {
      const prevNames = new Set(prevFiles.map(f => f.originalName));
      const filtered = renamedFiles.filter(f => !prevNames.has(f.originalName));
      const mapped = filtered.map(f => ({
        file: f.file,
        progress: 0,
        uploading: false,
        originalName: f.originalName
      }));
      return reindexFiles([...prevFiles, ...mapped]);
    });
    setSuccess(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getCurrentDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
  };

  const buildFileName = (location, index, extension) => {
    const locationName = locations.find(x => x.id === location)?.name;
    return `${locationName}_${index}_${getCurrentDate()}.${extension}`.replace(/ /g, '_');
  };

  const removeFile = fileName => {
    setSelectedFiles(prev => prev.filter(f => f.file.name !== fileName));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    if (!mission && (!selectedFiles || selectedFiles.length === 0)) return;
    if (selectedDeviceInfo) {
      const allFilesMatchDevice = selectedFiles.every(
        item => item.file.type === selectedDeviceInfo.accepted_type
      );
      if (!allFilesMatchDevice) {
        events.toggleErrorModal(
          `Selected files does not match to device accepted type: ${selectedDeviceInfo.accepted_type}`
        );
        return;
      }
    }
    if (mission && selectedFiles.length === 0) {
      API.upload
        .saveFileURL(
          customer,
          location,
          mission,
          '',
          selectedPlant,
          selectedCropPipeline,
          selectedDevice
        )
        .then(() => {
          if (onUploadComplete) {
            onUploadComplete();
          }
        });
    }
    setUploading(true);

    const mission_id = mission === undefined ? generateMissionId() : mission;
    let completedCount = 0;
    let filesUrl;
    selectedFiles.forEach((selectedFile, index) => {
      uploadFile(selectedFile, index, mission_id)
        .then(file_url => {
          filesUrl = file_url;
        })
        .catch(err => {
          console.error('Upload failed:', err);
          setSuccess(false);
        })
        .finally(() => {
          completedCount++;
          if (completedCount === selectedFiles.length) {
            API.upload
              .saveFileURL(
                customer,
                location,
                mission_id,
                filesUrl,
                selectedPlant,
                selectedCropPipeline,
                selectedDevice
              )
              .then(() => {
                if (onUploadComplete) {
                  onUploadComplete();
                }
              });
            setUploading(false);
            setSelectedFiles([]);
            setSuccess(true);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }
        });
    });
  };

  return (
    <FileContainer>
      <TitleContainer>
        {mission === undefined ? (
          <Title>Upload a New Mission Data</Title>
        ) : (
          <Title>Upload files to Mission {mission}</Title>
        )}
        <UploadContainer>
          {selectedFiles.length === 0 && (
            <UploadInfo>
              <HelpText>There are no files for upload.</HelpText>
              {/* eslint-disable-next-line react/no-unescaped-entities */}
              <HelpText>Press the "Add file..." button to add the files</HelpText>
            </UploadInfo>
          )}
          {selectedFiles.length > 0 && !uploading && (
            <UploadInfo>
              <HelpText style={{ color: '#d9822b' }} maxWidth={800}>
                Please make sure videos are selected in the correct order (first → last),
                it will affect on quality of processing
              </HelpText>
            </UploadInfo>
          )}
          {success && selectedFiles.length === 0 && (
            <HelpText>Files was uploaded successfully</HelpText>
          )}
          {selectedFiles.length !== 0 && (
            <UploadFiles>
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={selectedFiles.map(f => f.file.name)}
                  strategy={verticalListSortingStrategy}
                >
                  {selectedFiles.map((selectedFile, index) => (
                    <SortableItem
                      key={selectedFile.file.name}
                      id={selectedFile.file.name}
                    >
                      <UploadFileItem>
                        <HelpText title={selectedFile.file.name} maxWidth={600}>
                          <strong>{index + 1}.</strong> {selectedFile.file.name}{' '}
                          (Originally {selectedFile.originalName})
                        </HelpText>
                        <UploadFileItemInfo>
                          <HelpText>
                            {(selectedFile.file.size / (1024 * 1024)).toFixed(2)} MB
                          </HelpText>
                          {!uploading && (
                            <RemoveButton
                              onClick={() => removeFile(selectedFile.file.name)}
                            >
                              <Icon>{'Delete'}</Icon>
                            </RemoveButton>
                          )}
                          {selectedFile.uploading && (
                            <ProgressBarItem>
                              <ProgressBarContainer>
                                <ProgressBarFill
                                  $width={selectedFile.progress}
                                ></ProgressBarFill>
                                <ProgressBarText>
                                  {selectedFile.progress}%
                                </ProgressBarText>
                              </ProgressBarContainer>
                            </ProgressBarItem>
                          )}
                        </UploadFileItemInfo>
                      </UploadFileItem>
                    </SortableItem>
                  ))}
                </SortableContext>
              </DndContext>
            </UploadFiles>
          )}
        </UploadContainer>
        {devices.length === 0 && (
          <WarningText>
            Device is not selected for your location, please contact support
          </WarningText>
        )}
        <ButtonContainer>
          <StyledSelect
            items={plants.map(item => ({
              name: item
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' '),
              value: item
            }))}
            onSelect={({ value }) => setSelectedPlant(value)}
            value={selectedPlant ? selectedPlant : plant}
            placeholder={'Select Plant'}
          />
          <StyledSelect
            items={cropPipelines.map(item => ({
              name: item
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' '),
              value: item
            }))}
            onSelect={({ value }) => setSelectedCropPipeline(value)}
            value={selectedCropPipeline ? selectedCropPipeline : cropPipeline}
            placeholder={'Select Service'}
          />
          <StyledSelect
            items={devices.map(item => ({
              name: item.name
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' '),
              value: item.name
            }))}
            onSelect={({ value }) => setSelectedDevice(value)}
            value={selectedDevice ? selectedDevice : device}
            placeholder={'Select Device'}
          />
          <UploadLabel
            htmlFor={`uploader-${location}-${customer}-${mission || 'general'}`}
          >
            <i className='fa fa-cloud-upload'></i> <Icon>{'Plus'}</Icon> Add Files
          </UploadLabel>
          {uploading && selectedFiles.length !== 0 ? (
            <StyledButton onClick={cancelUpload} isActive={true}>
              Cancel Upload
            </StyledButton>
          ) : (
            <StartButton
              title='Plant and Service must be selected'
              onClick={handleUploadClick}
              disabled={
                !mission &&
                (uploading ||
                  selectedFiles.length === 0 ||
                  !selectedPlant ||
                  !selectedCropPipeline ||
                  !selectedDevice)
              }
              isActive={true}
              icon={<Icon>{'Upload'}</Icon>}
            >
              {mission ? 'Save' : 'Upload'}
            </StartButton>
          )}
        </ButtonContainer>
        <input
          id={`uploader-${location}-${customer}-${mission || 'general'}`}
          type='file'
          multiple={true}
          accept='.mp4,.zip'
          onChange={handleFileChange}
          ref={fileInputRef}
          disabled={uploading}
          style={{ display: 'none' }}
        />
      </TitleContainer>
    </FileContainer>
  );
};
